import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader, EmptyState, ErrorState } from '@ui-kit';
import { useSettings } from '../hooks/useSettings';
import { useCategories } from '../hooks/useCategories';
import { useUpdateSetting } from '../hooks/useUpdateSetting';
import { useResetSetting } from '../hooks/useResetSetting';
import { SettingCategoryTabs, SettingCategoryTabsSkeleton } from '../components/SettingCategoryTabs';
import { SettingCard, SettingCardSkeleton } from '../components/SettingCard';
import { SettingEditDialog } from '../components/SettingEditDialog';
import { SettingResetConfirm } from '../components/SettingResetConfirm';
import type { SystemSettingResponse, SettingCategory, ApiError } from '../types/setting';


const FALLBACK_CATEGORIES: SettingCategory[] = [
  'SECURITY',
  'PERFORMANCE',
  'APPEARANCE',
  'LOGGING',
];

/* ---------- Simple toast ------------------------------------------ */
interface ToastMsg {
  id: number;
  text: string;
  type: 'success' | 'error';
}
let _toastId = 0;

function Toast({ messages, onDismiss }: { messages: ToastMsg[]; onDismiss: (id: number) => void }) {
  if (messages.length === 0) return null;
  return (
    <div
      aria-live="polite"
      aria-atomic="false"
      style={{
        position: 'fixed',
        bottom: 'var(--spacing-6)',
        right: 'var(--spacing-6)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--spacing-2)',
        zIndex: 2000,
      }}
    >
      {messages.map((msg) => (
        <div
          key={msg.id}
          role="status"
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderRadius: 'var(--radius-md)',
            background:
              msg.type === 'success' ? 'var(--color-success)' : 'var(--color-danger)',
            color: 'var(--color-text-inverse)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 600,
            boxShadow: 'var(--shadow-md)',
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            maxWidth: 360,
            cursor: 'pointer',
          }}
          onClick={() => onDismiss(msg.id)}
        >
          <span>{msg.text}</span>
          <span style={{ marginLeft: 'auto', opacity: 0.7, fontSize: 'var(--font-size-xs)' }}>
            ✕
          </span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* CommonUsagePanel — biz-foundation §4.11                             */
/* ------------------------------------------------------------------ */
function CommonUsagePanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const modules = [
    { id: 'C001', name: 'BusinessException', role: '도메인 예외 (SETTING-404/422/409)' },
    { id: 'C002', name: 'ApiResponse<T>', role: '모든 응답 래핑' },
    { id: 'C006', name: 'RequestContext.userId()', role: '감사 actor 자동 (updated_by)' },
    { id: 'C008', name: 'EventPublisher.publish()', role: 'SystemSettingChangedEvent 발행' },
    { id: 'C014', name: '@Audited(action="CONFIG_CHANGED")', role: '모든 mutation 자동 감사' },
    { id: 'C028', name: 'JwtAuthenticationService', role: 'Bearer 인증 (필터 자동)' },
    { id: 'C029', name: 'SecurityChainCustomizer', role: '/api/eraf/system-settings/** ROLE_ADMIN' },
    { id: 'C030', name: '@HasPermission("SYSTEM_SETTING_WRITE")', role: '메서드 단위 권한 이중 방어' },
    { id: 'C041', name: 'SpecificationBuilder', role: 'category/keyword 동적 쿼리' },
    { id: 'C042', name: 'GenericRepository', role: 'SystemSettingRepository 기반' },
  ];
  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--color-surface-muted)',
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls="common-usage-panel"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-2)',
          width: '100%',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-sans)',
          textAlign: 'left',
        }}
      >
        <span style={{ transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'none' }}>
          ▾
        </span>
        {t('systemSettings.commonUsage.title')} ({open ? t('systemSettings.edit.button.cancel') : t('systemSettings.card.button.edit')})
      </button>
      {open && (
        <div
          id="common-usage-panel"
          style={{
            padding: 'var(--spacing-4)',
            borderTop: '1px solid var(--color-surface-border)',
          }}
        >
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <thead>
              <tr>
                {[t('systemSettings.card.label.dataType'), 'API', t('systemSettings.commonUsage.description')].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-2)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {modules.map((m) => (
                <tr key={m.id}>
                  <td
                    style={{
                      padding: 'var(--spacing-2)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-brand-primary)',
                    }}
                  >
                    {m.id}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      fontFamily: 'var(--font-family-mono)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    {m.name}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {m.role}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main page                                                            */
/* ------------------------------------------------------------------ */

export function SystemSettingsPage() {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState<SettingCategory>('SECURITY');
  const [keyword, setKeyword] = useState('');
  const [appliedKeyword, setAppliedKeyword] = useState('');

  // R9-E-003 fix (R7-091 / R8-E-003 3 rounds STILL_BROKEN):
  // 검색을 자동 적용 (debounce 250ms) — 사용자가 [검색] 버튼 클릭하지 않아도 동작.
  // 이전: appliedKeyword 가 [검색] 버튼 클릭에서만 갱신되어 사용자 혼동 + 검색 미작동 인상.
  useEffect(() => {
    const t = setTimeout(() => setAppliedKeyword(keyword.trim()), 250);
    return () => clearTimeout(t);
  }, [keyword]);

  // Dialog state
  const [editTarget, setEditTarget] = useState<SystemSettingResponse | null>(null);
  const [editError, setEditError] = useState<string | undefined>();
  const [editErrorCode, setEditErrorCode] = useState<string | undefined>();
  const [resetTarget, setResetTarget] = useState<SystemSettingResponse | null>(null);

  // Toast
  const [toasts, setToasts] = useState<ToastMsg[]>([]);
  const addToast = useCallback((text: string, type: 'success' | 'error' = 'success') => {
    const id = ++_toastId;
    setToasts((prev) => [...prev, { id, text, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 4000);
  }, []);
  const dismissToast = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Data hooks
  const FALLBACK_CATEGORY_LABELS: Record<SettingCategory, string> = {
    SECURITY: t('systemSettings.tab.SECURITY'),
    PERFORMANCE: t('systemSettings.tab.PERFORMANCE'),
    APPEARANCE: t('systemSettings.tab.APPEARANCE'),
    LOGGING: t('systemSettings.tab.LOGGING'),
  };
  const categoriesQuery = useCategories(i18n.language);
  const settingsQuery = useSettings(
    appliedKeyword ? { keyword: appliedKeyword } : undefined
  );
  const updateMutation = useUpdateSetting();
  const resetMutation = useResetSetting();

  const isLoading = settingsQuery.isLoading;
  const isError = settingsQuery.isError;

  // Build tab list from categories query or fallback
  const tabs = useMemo(() => {
    if (categoriesQuery.data && categoriesQuery.data.length > 0) {
      return categoriesQuery.data.map((cat) => ({
        category: cat.category,
        label: cat.label,
        itemCount: cat.itemCount,
      }));
    }
    // Fallback from settings data
    if (settingsQuery.data) {
      return settingsQuery.data.map((group) => ({
        category: group.category,
        label: FALLBACK_CATEGORY_LABELS[group.category] ?? group.category,
        itemCount: group.items.length,
      }));
    }
    // Skeleton fallback
    return FALLBACK_CATEGORIES.map((cat) => ({
      category: cat,
      label: FALLBACK_CATEGORY_LABELS[cat],
      itemCount: 0,
    }));
  }, [categoriesQuery.data, settingsQuery.data]);

  // Client-side keyword filter helper.
  // R9-E-003 강화: settingKey + description 외 labelKey · dataType · category 도 검색 (검색 회수율 향상).
  const matchesKeyword = useCallback(
    (item: {
      settingKey: string;
      description?: string | null;
      labelKey?: string | null;
      dataType?: string | null;
      category?: string | null;
    }) => {
      if (!appliedKeyword) return true;
      const q = appliedKeyword.toLowerCase();
      return (
        item.settingKey.toLowerCase().includes(q) ||
        (item.description ?? '').toLowerCase().includes(q) ||
        (item.labelKey ?? '').toLowerCase().includes(q) ||
        (item.dataType ?? '').toLowerCase().includes(q) ||
        (item.category ?? '').toLowerCase().includes(q)
      );
    },
    [appliedKeyword]
  );

  // Filtered items for active category (client-side keyword filter applied as safety net)
  const activeItems = useMemo(() => {
    if (!settingsQuery.data) return [];
    const group = settingsQuery.data.find((g) => g.category === activeCategory);
    return (group?.items ?? []).filter(matchesKeyword);
  }, [settingsQuery.data, activeCategory, matchesKeyword]);

  // All items across all categories with client-side keyword filter (for subtitle count + isSearchEmpty)
  const totalItems = useMemo(() => {
    if (!settingsQuery.data) return 0;
    return settingsQuery.data.reduce(
      (acc, g) => acc + g.items.filter(matchesKeyword).length,
      0
    );
  }, [settingsQuery.data, matchesKeyword]);

  // Last change info (across all items)
  const lastChanged = useMemo(() => {
    if (!settingsQuery.data) return null;
    let latest: SystemSettingResponse | null = null;
    for (const group of settingsQuery.data) {
      for (const item of group.items) {
        if (item.updatedAt) {
          if (!latest || item.updatedAt > (latest.updatedAt ?? '')) latest = item;
        }
      }
    }
    return latest;
  }, [settingsQuery.data]);

  const handleSearch = useCallback(() => {
    setAppliedKeyword(keyword.trim());
  }, [keyword]);

  const handleClearSearch = useCallback(() => {
    setKeyword('');
    setAppliedKeyword('');
  }, []);

  // Edit handlers
  const handleEditOpen = useCallback((setting: SystemSettingResponse) => {
    setEditError(undefined);
    setEditErrorCode(undefined);
    setEditTarget(setting);
  }, []);

  const handleEditClose = useCallback(() => {
    setEditTarget(null);
    setEditError(undefined);
    setEditErrorCode(undefined);
  }, []);

  const handleEditSubmit = useCallback(
    (key: string, value: string) => {
      setEditError(undefined);
      setEditErrorCode(undefined);
      updateMutation.mutate(
        { key, body: { value } },
        {
          onSuccess: () => {
            setEditTarget(null);
            addToast(t('systemSettings.toast.updated', { settingKey: key }));
          },
          onError: (err: unknown) => {
            const apiErr = err as ApiError;
            setEditError(apiErr.message ?? t('systemSettings.error.network'));
            setEditErrorCode(apiErr.code);
          },
        }
      );
    },
    [updateMutation, addToast]
  );

  // Reset handlers
  const handleResetOpen = useCallback((setting: SystemSettingResponse) => {
    setResetTarget(setting);
  }, []);

  const handleResetClose = useCallback(() => {
    setResetTarget(null);
  }, []);

  const handleResetConfirm = useCallback(
    (key: string) => {
      resetMutation.mutate(key, {
        onSuccess: () => {
          setResetTarget(null);
          addToast(t('systemSettings.toast.reset', { settingKey: key }));
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          setResetTarget(null);
          if (apiErr.code === 'SETTING-409-001') {
            addToast(t('systemSettings.error.SETTING-409-001'), 'error');
          } else {
            addToast(apiErr.message ?? t('systemSettings.error.network'), 'error');
          }
        },
      });
    },
    [resetMutation, addToast]
  );

  // isSearchEmpty: API returned 0 items across all categories for this keyword
  const isSearchEmpty = !isLoading && !isError && !!appliedKeyword && totalItems === 0;
  // isActiveCategoryEmpty: items loaded but active tab has 0 results (possibly filtered)
  const isActiveCategoryEmpty = !isLoading && !isError && !isSearchEmpty && activeItems.length === 0;
  const showCards = !isLoading && !isError && !isSearchEmpty && activeItems.length > 0;

  const inputStyle: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-3)',
    background: 'var(--color-surface-elevated)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    color: 'var(--color-text-primary)',
    fontFamily: 'var(--font-family-sans)',
    outline: 'none',
    flex: 1,
    minWidth: 0,
  };

  const btnPrimary: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
    whiteSpace: 'nowrap',
  };

  const btnSecondary: React.CSSProperties = {
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-surface-elevated)',
    color: 'var(--color-text-primary)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
    whiteSpace: 'nowrap',
  };

  return (
    <div
      style={{
        padding: 'var(--spacing-6)',
        maxWidth: 1200,
        margin: '0 auto',
      }}
    >
      {/* Page header */}
      <PageHeader
        title={t('systemSettings.list.title')}
        breadcrumb={<span>{t('systemSettings.list.title')}</span>}
      />

      {/* Subtitle */}
      {settingsQuery.data && (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-secondary)',
            margin: 'var(--spacing-2) 0 var(--spacing-4)',
          }}
        >
          {t('systemSettings.list.subtitle', {
            categoryCount: tabs.length,
            totalItems,
            lastChangedAt: lastChanged?.updatedAt
              ? new Intl.DateTimeFormat(
                  i18n.language === 'en' ? 'en-US' : 'ko-KR',
                  { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }
                ).format(new Date(lastChanged.updatedAt))
              : '-',
            lastChangedBy: lastChanged?.updatedBy ?? '-',
          })}
        </p>
      )}

      {/* Category tabs */}
      {isLoading ? (
        <SettingCategoryTabsSkeleton />
      ) : (
        <SettingCategoryTabs
          tabs={tabs}
          activeCategory={activeCategory}
          onChange={setActiveCategory}
        />
      )}

      {/* Search bar */}
      <div
        role="search"
        aria-label={t('systemSettings.list.title')}
        style={{
          display: 'flex',
          gap: 'var(--spacing-2)',
          marginBottom: 'var(--spacing-4)',
          flexWrap: 'wrap',
        }}
      >
        <label htmlFor="setting-search" style={{ display: 'none' }}>
          {t('systemSettings.list.title')}
        </label>
        <input
          id="setting-search"
          type="search"
          placeholder={t('systemSettings.list.search.placeholder')}
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          style={inputStyle}
        />
        <button type="button" onClick={handleSearch} style={btnPrimary}>
          {t('common.action.search')}
        </button>
        {appliedKeyword && (
          <button type="button" onClick={handleClearSearch} style={btnSecondary}>
            {t('common.action.reset')}
          </button>
        )}
      </div>

      {/* Content area — 5 states */}
      <div
        id={`tabpanel-${activeCategory}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeCategory}`}
      >
        {/* loading — skeleton cards */}
        {isLoading && (
          <div aria-label={t('systemSettings.list.title')} aria-busy="true">
            <SettingCardSkeleton />
            <SettingCardSkeleton />
            <SettingCardSkeleton />
          </div>
        )}

        {/* error */}
        {isError && (
          <ErrorState
            title={t('systemSettings.error.network')}
            message={
              (settingsQuery.error as unknown as ApiError)?.code === 'AUTH-403-001'
                ? t('systemSettings.error.SETTING-403-001')
                : ((settingsQuery.error as unknown as ApiError)?.message ??
                  t('systemSettings.error.network'))
            }
            onRetry={() => void settingsQuery.refetch()}
          />
        )}

        {/* empty (search result empty) */}
        {isSearchEmpty && (
          <EmptyState
            title={t('systemSettings.list.empty.title')}
            description={t('systemSettings.list.empty.description')}
            action={
              <button type="button" onClick={handleClearSearch} style={btnSecondary}>
                {t('systemSettings.list.empty.button.clear')}
              </button>
            }
          />
        )}

        {/* active-category empty (search filtered out all items in this tab) */}
        {isActiveCategoryEmpty && (
          <EmptyState
            title={t('systemSettings.list.empty.title')}
            description={t('systemSettings.list.empty.description')}
            action={
              appliedKeyword ? (
                <button type="button" onClick={handleClearSearch} style={btnSecondary}>
                  {t('systemSettings.list.empty.button.clear')}
                </button>
              ) : undefined
            }
          />
        )}

        {/* default — setting cards */}
        {showCards && (
          activeItems.map((item) => (
            <SettingCard
              key={item.settingKey}
              setting={item}
              onEdit={handleEditOpen}
              onReset={handleResetOpen}
            />
          ))
        )}
      </div>

      {/* Common usage panel */}
      <CommonUsagePanel />

      {/* Edit dialog — dialog state */}
      <SettingEditDialog
        open={editTarget !== null}
        setting={editTarget}
        isLoading={updateMutation.isPending}
        errorMessage={editError}
        errorCode={editErrorCode}
        onClose={handleEditClose}
        onSubmit={handleEditSubmit}
      />

      {/* Reset confirm dialog — dialog state */}
      <SettingResetConfirm
        open={resetTarget !== null}
        setting={resetTarget}
        isLoading={resetMutation.isPending}
        onClose={handleResetClose}
        onConfirm={handleResetConfirm}
      />

      {/* Toast notifications */}
      <Toast messages={toasts} onDismiss={dismissToast} />
    </div>
  );
}
